import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import { format } from "date-fns";
import usePickup from "hooks/pickups/usePickup";
import usePickupTracking from "hooks/pickups/usePickupTracking";
import { useTerminology } from "hooks/useTerminology";
import { isDrmaOrganization } from "lib/organizationType";
import { useMemo } from "react";
import { Link as RouterLink, Navigate } from "react-router-dom";
import PickupTrackingMap from "./PickupTrackingMap";

function formatTrackingDate(value?: string | null) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return format(parsed, "MMM d, yyyy h:mm a");
}

export default function PickupTracking() {
  const terminology = useTerminology();
  const { pickup, isLoading: pickupLoading } = usePickup();
  const {
    data: tracking,
    isLoading: trackingLoading,
    isError,
    refetch,
    isFetching,
  } = usePickupTracking();

  const devicePosition = useMemo(() => {
    const lat = tracking?.device?.latitude;
    const lng = tracking?.device?.longitude;
    if (lat == null || lng == null) return null;
    return { lat: Number(lat), lng: Number(lng) };
  }, [tracking?.device?.latitude, tracking?.device?.longitude]);

  if (!isDrmaOrganization()) {
    return <Navigate to="/pickups" replace />;
  }

  if (pickupLoading || trackingLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Box>
        <Breadcrumbs
          data={[
            { label: "Home", to: "/" },
            { label: terminology.Pickups, to: "/pickups" },
            { label: "Live tracking" },
          ]}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 2,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Live tracking
        </Typography>
        {isFetching && !trackingLoading && (
          <Typography variant="caption" color="text.secondary">
            Updating…
          </Typography>
        )}
      </Box>

      {pickup && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {terminology.Pickup} #{pickup.orderNumber}
          {pickup.scheduledDate ? ` · ${pickup.scheduledDate}` : ""}
        </Typography>
      )}

      {isError && (
        <Alert
          severity="error"
          sx={{ mt: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Tracking is temporarily unavailable. Please try again in a moment.
        </Alert>
      )}

      {!isError && tracking && !tracking.available && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Tracking is not available yet for this {terminology.pickup}. Our team
          will enable live tracking once your shipment is on the way.
        </Alert>
      )}

      {!isError && tracking?.available && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {tracking.shipment?.status && (
              <Chip label={tracking.shipment.status} color="primary" />
            )}
            {tracking.shipment?.name && (
              <Chip label={tracking.shipment.name} variant="outlined" />
            )}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            {tracking.shipment?.eta != null && (
              <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, boxShadow: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Estimated arrival
                </Typography>
                <Typography variant="body1">
                  {formatTrackingDate(tracking.shipment?.eta)}
                </Typography>
              </Box>
            )}
            {(tracking.shipment?.startAt != null || tracking.shipment?.endAt != null) && (
              <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, boxShadow: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Delivery window
                </Typography>
                <Typography variant="body2">
                  {formatTrackingDate(tracking.shipment?.startAt)} –{" "}
                  {formatTrackingDate(tracking.shipment?.endAt)}
                </Typography>
              </Box>
            )}
            {pickup?.scheduledDate && !tracking.shipment && (
              <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, boxShadow: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {terminology.Pickup} date
                </Typography>
                <Typography variant="body1">{pickup.scheduledDate}</Typography>
              </Box>
            )}
            <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Current location
              </Typography>
              <Typography variant="body1">
                {tracking.device?.address || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Last update: {formatTrackingDate(tracking.device?.lastPositionAt)}
              </Typography>
              {tracking.device?.batteryPercentage != null && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Tracker battery: {tracking.device.batteryPercentage}%
                </Typography>
              )}
            </Box>
            {tracking.device?.name && (
              <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, boxShadow: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Tracker
                </Typography>
                <Typography variant="body1">{tracking.device.name}</Typography>
              </Box>
            )}
          </Box>

          <PickupTrackingMap
            positions={tracking.positions || []}
            devicePosition={devicePosition}
            startLocation={tracking.shipment?.startLocation}
            endLocation={tracking.shipment?.endLocation}
            pickupLocation={pickup?.location}
          />
        </Box>
      )}

      <Button component={RouterLink} to="/pickups" sx={{ mt: 3 }}>
        Back to {terminology.pickups}
      </Button>
    </div>
  );
}
